// src/pages/SuccessStories.tsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { 
  successStories, 
  getFeaturedStories,
  successStoriesStats,
  type SuccessStory
} from '@/data/mock/successStories';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, 
  MapPin, 
  Star, 
  Award,
  Target,
  Sparkles,
  TrendingUp,
  Users,
  Heart,
  Flag,
  Calendar,
  Medal,
  Quote,
  ExternalLink,
  Play,
  Instagram,
  Twitter,
  Facebook,
  ChevronRight,
  Filter,
  Search,
  X,
  BarChart3,
  Crown,
  Zap,
  Flame,
  Globe
} from 'lucide-react';

const SuccessStories = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [selectedSport, setSelectedSport] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStory, setSelectedStory] = useState<SuccessStory | null>(null);

  // Get unique sports for filtering
  const sports = useMemo(() => {
    const uniqueSports = Array.from(new Set(successStories.map(s => s.sport.en)));
    return ['all', ...uniqueSports];
  }, []);

  const levels = ['all', 'Professional', 'Semi-Professional', 'National Team', 'International'];

  // Filter stories
  const filteredStories = useMemo(() => {
    return successStories.filter(story => {
      const matchesSport = selectedSport === 'all' || story.sport.en === selectedSport;
      const matchesLevel = selectedLevel === 'all' || story.level.en === selectedLevel;
      const matchesSearch = searchQuery === '' || 
        story.name.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
        story.name.ar.includes(searchQuery) ||
        story.sport.en.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesSport && matchesLevel && matchesSearch;
    });
  }, [selectedSport, selectedLevel, searchQuery]);

  // Featured stories
  const featuredStories = getFeaturedStories();

  // Statistics
  const stats = [
    {
      icon: Trophy,
      value: successStories.length.toString(),
      label: { en: 'Success Stories', ar: 'قصص نجاح' },
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Crown,
      value: successStoriesStats.byLevel.professional.toString(),
      label: { en: 'Professional Athletes', ar: 'رياضيون محترفون' },
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Flag,
      value: successStoriesStats.byLevel.nationalTeam.toString(),
      label: { en: 'National Team', ar: 'منتخب وطني' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Award,
      value: `${successStoriesStats.totalAchievements}+`,
      label: { en: 'Total Achievements', ar: 'إجمالي الإنجازات' },
      color: 'from-blue-500 to-cyan-500'
    },
  ];

  // Level badges config
  const getLevelBadge = (level: string) => {
    const badges = {
      'Professional': { color: 'from-purple-500 to-pink-500', icon: Crown, text: isRTL ? 'محترف' : 'Pro' },
      'Semi-Professional': { color: 'from-blue-500 to-cyan-500', icon: Star, text: isRTL ? 'شبه محترف' : 'Semi-Pro' },
      'National Team': { color: 'from-green-500 to-emerald-500', icon: Flag, text: isRTL ? 'منتخب' : 'National' },
      'International': { color: 'from-red-500 to-orange-500', icon: Globe, text: isRTL ? 'دولي' : 'International' }
    };
    return badges[level as keyof typeof badges] || badges['Professional'];
  };

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={t('successStories.title')}
        subtitle={isRTL
          ? 'رحلات ملهمة من أكاديميتنا إلى قمة الرياضة - قصص حقيقية لأبطال حقيقيين'
          : 'Inspiring journeys from our academy to the pinnacle of sports - real stories of real champions'
        }
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.successStories') }
        ]}
      />

      {/* Inspiration Banner */}
      <section className="py-6 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border-y border-primary/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-3 text-center flex-wrap"
          >
            <Sparkles className="h-6 w-6 text-yellow-500" />
            <p className="text-sm font-semibold text-foreground">
              {isRTL
                ? 'من تدريباتنا إلى المنصات العالمية - أحلام تحققت وأبطال صُنعوا'
                : 'From our training grounds to world stages - dreams realized and champions made'
              }
            </p>
            <Sparkles className="h-6 w-6 text-yellow-500" />
          </motion.div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <BarChart3 className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'إحصائيات' : 'Statistics'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'نجاحات يفخر بها' : 'Achievements to be Proud Of'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -8 }}
                className="p-8 rounded-3xl bg-card border-2 border-border hover:border-primary/30 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-4`}>
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-5xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Stories */}
      {featuredStories.length > 0 && (
        <section className="py-20 bg-surface-1">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
                <Star className="h-5 w-5 text-yellow-500" />
                <span className="text-sm font-semibold text-yellow-500">
                  {isRTL ? 'قصص مميزة' : 'Featured Stories'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'رحلات استثنائية' : 'Exceptional Journeys'}
              </h2>
            </motion.div>

            {featuredStories.map((story, idx) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="mb-12 last:mb-0"
              >
                <FeaturedStoryCard story={story} isRTL={isRTL} />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Filters & Search */}
      <section className="py-12 bg-gradient-to-b from-background to-surface-1 top-20 z-40 border-b border-border shadow-lg">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
                <input
                  type="text"
                  placeholder={isRTL ? 'ابحث عن رياضي...' : 'Search athlete...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full ${isRTL ? 'pr-12 pl-12' : 'pl-12 pr-12'} py-3 rounded-full bg-card border-2 border-border focus:border-primary/50 outline-none transition-all`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} w-6 h-6 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-all`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Sport Filter */}
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <select
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  className="px-6 py-3 rounded-full bg-card border-2 border-border focus:border-primary/50 outline-none cursor-pointer transition-all font-medium min-w-[150px]"
                >
                  <option value="all">{isRTL ? 'جميع الرياضات' : 'All Sports'}</option>
                  {sports.slice(1).map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>

              {/* Level Filter */}
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <select
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                  className="px-6 py-3 rounded-full bg-card border-2 border-border focus:border-primary/50 outline-none cursor-pointer transition-all font-medium min-w-[150px]"
                >
                  <option value="all">{isRTL ? 'جميع المستويات' : 'All Levels'}</option>
                  {levels.slice(1).map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results Count */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-center"
            >
              <span className="text-sm text-muted-foreground">
                {isRTL 
                  ? `عرض ${filteredStories.length} من ${successStories.length}`
                  : `Showing ${filteredStories.length} of ${successStories.length}`
                }
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Success Stories Grid */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          {filteredStories.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {filteredStories.map((story, idx) => (
                <StoryCard
                  key={story.id}
                  story={story}
                  isRTL={isRTL}
                  delay={idx * 0.1}
                  getLevelBadge={getLevelBadge}
                  onClick={() => setSelectedStory(story)}
                />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-lg text-muted-foreground">
                {isRTL ? 'لم يتم العثور على نتائج' : 'No results found'}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Inspiration Section */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/30 via-accent/20 to-transparent rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Icon */}
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 0],
              }}
              transition={{ duration: 4, repeat: Infinity }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 p-1 mb-8 shadow-2xl"
            >
              <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                <TrendingUp className="h-12 w-12 text-yellow-500" />
              </div>
            </motion.div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'قصتك التالية!' : 'Your Story is Next!'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              {isRTL
                ? 'كل بطل بدأ كطفل بحلم. انضم لأكاديمية تشامبيونز واكتب قصة نجاحك الخاصة'
                : 'Every champion started as a child with a dream. Join Champions Academy and write your own success story'
              }
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="px-12 py-7 text-lg rounded-full btn-glow">
                <Target className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'ابدأ رحلتك' : 'Start Your Journey'}
              </Button>

              <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                <Users className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'تواصل معنا' : 'Contact Us'}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Detail Modal */}
      <AnimatePresence>
        {selectedStory && (
          <StoryDetailModal
            story={selectedStory}
            isRTL={isRTL}
            getLevelBadge={getLevelBadge}
            onClose={() => setSelectedStory(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Featured Story Card Component
const FeaturedStoryCard = ({ story, isRTL }: { story: SuccessStory; isRTL: boolean }) => {
  return (
    <div className="grid lg:grid-cols-2 gap-8 p-8 rounded-3xl bg-card border-2 border-primary/20 shadow-2xl overflow-hidden relative">
      {/* Featured Badge */}
      <div className="absolute top-6 right-6 z-10">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/30">
          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
          <span className="text-xs font-bold text-yellow-500">
            {isRTL ? 'مميزة' : 'FEATURED'}
          </span>
        </div>
      </div>

      {/* Image Side */}
      <div className="relative">
        <div className="aspect-[3/4] rounded-2xl overflow-hidden">
          <img
            src={story.image}
            alt={story.name[isRTL ? 'ar' : 'en']}
            className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
            loading="lazy"
          />
        </div>

        {/* Stats Overlay */}
        {story.stats && (
          <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-card/95 backdrop-blur-sm border border-border">
            <div className="grid grid-cols-2 gap-3">
              {story.stats.slice(0, 4).map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-2xl font-bold gradient-text">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label[isRTL ? 'ar' : 'en']}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Content Side */}
      <div className="flex flex-col justify-center">
        <h3 className="text-4xl font-bold mb-3">{story.name[isRTL ? 'ar' : 'en']}</h3>
        
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-2 text-primary font-semibold">
            <Trophy className="h-5 w-5" />
            <span>{story.sport[isRTL ? 'ar' : 'en']}</span>
          </div>
          {story.jerseyNumber && (
            <div className="w-10 h-10 rounded-lg bg-primary/10 border-2 border-primary/30 flex items-center justify-center font-bold">
              {story.jerseyNumber}
            </div>
          )}
        </div>

        {story.currentClub && (
          <div className="flex items-center gap-2 text-muted-foreground mb-6">
            <MapPin className="h-4 w-4" />
            <span className="text-sm">{story.currentClub[isRTL ? 'ar' : 'en']}</span>
          </div>
        )}

        <p className="text-lg text-muted-foreground mb-6 leading-relaxed whitespace-pre-line">
          {story.story[isRTL ? 'ar' : 'en']}
        </p>

        {story.quote && (
          <div className="p-6 rounded-2xl bg-primary/5 border-l-4 border-primary mb-6">
            <Quote className="h-6 w-6 text-primary/50 mb-3" />
            <p className="text-foreground italic leading-relaxed">
              "{story.quote[isRTL ? 'ar' : 'en']}"
            </p>
          </div>
        )}

        {story.achievements && (
          <div className="border-t border-border pt-6">
            <div className="flex items-center gap-2 text-sm font-semibold mb-4">
              <Medal className="h-5 w-5 text-primary" />
              <span>{isRTL ? 'الإنجازات البارزة' : 'Key Achievements'}</span>
            </div>
            <ul className="space-y-2">
              {story.achievements[isRTL ? 'ar' : 'en'].slice(0, 5).map((achievement, idx) => (
                <li key={idx} className="text-sm text-muted-foreground flex items-start gap-3">
                  <ChevronRight className={`h-4 w-4 text-primary mt-0.5 flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
                  <span>{achievement}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// Story Card Component
const StoryCard = ({ 
  story, 
  isRTL, 
  delay, 
  getLevelBadge,
  onClick
}: { 
  story: SuccessStory; 
  isRTL: boolean; 
  delay: number;
  getLevelBadge: (level: string) => any;
  onClick: () => void;
}) => {
  const levelBadge = getLevelBadge(story.level.en);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ y: -8 }}
      onClick={onClick}
      className="bg-card border-2 border-border rounded-3xl overflow-hidden hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer group"
    >
      {/* Image */}
      <div className="aspect-video bg-muted relative overflow-hidden">
        <img
          src={story.image}
          alt={story.name[isRTL ? 'ar' : 'en']}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          loading="lazy"
        />
        
        {/* Level Badge */}
        <div className="absolute top-4 left-4">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${levelBadge.color} backdrop-blur-sm`}>
            <levelBadge.icon className="h-4 w-4 text-white" />
            <span className="text-xs font-bold text-white">{levelBadge.text}</span>
          </div>
        </div>

        {/* Jersey Number */}
        {story.jerseyNumber && (
          <div className="absolute top-4 right-4">
            <div className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center font-black text-xl">
              {story.jerseyNumber}
            </div>
          </div>
        )}

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-center pb-6">
          <div className="flex items-center gap-2 text-white font-semibold">
            <span>{isRTL ? 'اقرأ القصة' : 'Read Story'}</span>
            <ChevronRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-2xl font-bold mb-2 group-hover:text-primary transition-colors">
          {story.name[isRTL ? 'ar' : 'en']}
        </h3>
        
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-2 text-primary font-medium">
            <Trophy className="h-4 w-4" />
            <span className="text-sm">{story.sport[isRTL ? 'ar' : 'en']}</span>
          </div>
        </div>

        {story.currentClub && (
          <div className="flex items-center gap-2 text-muted-foreground mb-4">
            <MapPin className="h-4 w-4" />
            <span className="text-sm">{story.currentClub[isRTL ? 'ar' : 'en']}</span>
          </div>
        )}

        <p className="text-muted-foreground mb-6 leading-relaxed line-clamp-3">
          {story.story[isRTL ? 'ar' : 'en']}
        </p>

        {story.achievements && story.achievements[isRTL ? 'ar' : 'en'].length > 0 && (
          <div className="border-t border-border pt-4">
            <div className="flex items-center gap-2 text-sm font-semibold mb-3">
              <Medal className="h-4 w-4 text-primary" />
              <span>{isRTL ? 'الإنجازات' : 'Achievements'}</span>
            </div>
            <ul className="space-y-2">
              {story.achievements[isRTL ? 'ar' : 'en'].slice(0, 3).map((achievement, idx) => (
                <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="line-clamp-1">{achievement}</span>
                </li>
              ))}
            </ul>
            {story.achievements[isRTL ? 'ar' : 'en'].length > 3 && (
              <p className="text-xs text-primary font-semibold mt-2">
                +{story.achievements[isRTL ? 'ar' : 'en'].length - 3} {isRTL ? 'المزيد' : 'more'}
              </p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Story Detail Modal Component
const StoryDetailModal = ({ 
  story, 
  isRTL, 
  getLevelBadge,
  onClose 
}: { 
  story: SuccessStory; 
  isRTL: boolean;
  getLevelBadge: (level: string) => any;
  onClose: () => void;
}) => {
  const levelBadge = getLevelBadge(story.level.en);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
    >
      <motion.div
        initial={{ scale: 0.9, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 50 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-card rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-card border-2 border-border hover:border-primary/50 flex items-center justify-center transition-all"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header Image */}
        <div className="aspect-video bg-muted relative overflow-hidden">
          <img
            src={story.image}
            alt={story.name[isRTL ? 'ar' : 'en']}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          
          {/* Level Badge */}
          <div className="absolute top-6 left-6">
            <div className={`flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r ${levelBadge.color}`}>
              <levelBadge.icon className="h-5 w-5 text-white" />
              <span className="text-sm font-bold text-white">{levelBadge.text}</span>
            </div>
          </div>

          {/* Name Overlay */}
          <div className="absolute bottom-6 left-6 right-6">
            <h2 className="text-4xl font-bold text-white mb-2">
              {story.name[isRTL ? 'ar' : 'en']}
            </h2>
            <div className="flex items-center gap-4 text-white">
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                <span>{story.sport[isRTL ? 'ar' : 'en']}</span>
              </div>
              {story.jerseyNumber && (
                <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center font-bold">
                  {story.jerseyNumber}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {story.currentClub && (
            <div className="flex items-center gap-2 text-muted-foreground mb-6">
              <MapPin className="h-5 w-5" />
              <span>{story.currentClub[isRTL ? 'ar' : 'en']}</span>
            </div>
          )}

          {/* Stats */}
          {story.stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {story.stats.map((stat, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-primary/5 border border-primary/20 text-center">
                  <div className="text-3xl mb-1">{stat.icon}</div>
                  <div className="text-2xl font-bold gradient-text mb-1">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label[isRTL ? 'ar' : 'en']}</div>
                </div>
              ))}
            </div>
          )}

          {/* Story */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Star className="h-6 w-6 text-primary" />
              {isRTL ? 'القصة' : 'The Story'}
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-line">
              {story.story[isRTL ? 'ar' : 'en']}
            </p>
          </div>

          {/* Quote */}
          {story.quote && (
            <div className="p-6 rounded-2xl bg-primary/5 border-l-4 border-primary mb-8">
              <Quote className="h-8 w-8 text-primary/50 mb-4" />
              <p className="text-xl text-foreground italic leading-relaxed">
                "{story.quote[isRTL ? 'ar' : 'en']}"
              </p>
            </div>
          )}

          {/* Achievements */}
          {story.achievements && (
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Medal className="h-6 w-6 text-primary" />
                {isRTL ? 'الإنجازات' : 'Achievements'}
              </h3>
              <ul className="space-y-3">
                {story.achievements[isRTL ? 'ar' : 'en'].map((achievement, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-muted-foreground">
                    <ChevronRight className={`h-5 w-5 text-primary mt-0.5 flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
                    <span>{achievement}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Timeline */}
          {story.timeline && (
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Calendar className="h-6 w-6 text-primary" />
                {isRTL ? 'المسيرة' : 'Career Timeline'}
              </h3>
              <div className="space-y-4">
                {story.timeline.map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-12 h-12 rounded-full ${item.highlight ? 'bg-primary' : 'bg-muted'} flex items-center justify-center font-bold text-sm ${item.highlight ? 'text-white' : 'text-muted-foreground'}`}>
                        {item.year}
                      </div>
                      {idx < story.timeline!.length - 1 && (
                        <div className="w-0.5 h-12 bg-border mt-2" />
                      )}
                    </div>
                    <div className={`flex-1 p-4 rounded-xl ${item.highlight ? 'bg-primary/10 border-2 border-primary/30' : 'bg-muted'} mt-2`}>
                      <p className="text-sm font-medium">
                        {item.event[isRTL ? 'ar' : 'en']}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Social Media */}
          {story.socialMedia && (
            <div className="flex items-center justify-center gap-4 pt-8 border-t border-border">
              {story.socialMedia.instagram && (
                <a href={story.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {story.socialMedia.twitter && (
                <a href={story.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all">
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {story.socialMedia.facebook && (
                <a href={story.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SuccessStories;
